import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Row, Col, Card, Collapse } from 'react-bootstrap';
import * as d3 from 'd3';

export interface TotalRevenueLong {
  sf_id: string;
  sf_name: string;
  report_year: number;
  net_amount: number;
  net_amount_l: number;
  net_amount_lc: number;
  net_amount_lcp: number;
  net_amount_l6: number;
  net_amount_lc6: number;
  net_amount_lcp6: number;
  net_amount_inflation: number;
  net_amount_inflation_l: number;
  net_amount_inflation_lc: number;
  net_amount_inflation_lcp: number;
  net_amount_inflation_l6: number;
  net_amount_inflation_lc6: number;
  net_amount_inflation_lcp6: number;
  census_population: number;
  per_capita_metric: number;
  public_explanation: string;
}

interface DerivedRow extends TotalRevenueLong {
  _amt: number;
  _amtL: number | null;
  _amtLc: number | null;
  _amtLcp: number | null;
  _amtL6: number | null;
  _amtLc6: number | null;
  _amtLcp6: number | null;
}

interface EntityTotalRevenueOverviewProps {
  data: TotalRevenueLong[];
  loading: boolean;
  parentEntityName?: string;
  parentEntityId: number | null;
  formatCurrency: (value: number) => string;
  formatEntityString: (value: string) => string;
  activeTab: string;
  entityPopulationData?: EntityPopulation[];
}

interface EntityPopulation {
  census_population_long_id: string;
  entity_id: string;
  entity_name: string;
  census_year: number;
  census_population: number;
  census_summary_level: string;
  census_state_fips_code: string;
  census_county_fips_code: string;
  census_place_fips_code: string;
  census_area_name: string;
}

const sortAsc = <T extends { report_year: number }>(arr: T[]) =>
  [...arr].sort((a, b) => a.report_year - b.report_year);

const sortDesc = <T extends { report_year: number }>(arr: T[]) =>
  [...arr].sort((a, b) => b.report_year - a.report_year);

const BRAND = {
  teal: '#2E8B8B',
  tealDark: '#236b6b',
  yellow: '#ffc63a',
  black: '#000000',
  grey: '#718096',
  mutedGrey: '#4A5568',
  white: '#ffffff',
  red: '#E53E3E',
  green: '#38A169',
};

const MARGIN = { top: 28, right: 24, bottom: 48, left: 72 };
const MARGIN_SM = { top: 20, right: 20, bottom: 44, left: 64 };

const safeNum = (v: any): number | null => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
};

const fmtAxisDollar = (v: number): string => {
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const fmtShortDollar = (v: number): string => {
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `${sign}$${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const fmtPct = (v: number | null): string => {
  if (v === null) return 'N/A';
  return `${(v * 100).toFixed(2)}%`;
};

const hasNote = (d: DerivedRow): boolean => {
  return !!d.public_explanation && d.public_explanation.trim() !== '' && d.public_explanation !== 'NaN';
};

const noteHtml = (d: DerivedRow): string => {
  if (!hasNote(d)) return '';
  const escaped = d.public_explanation.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<div class="tu-d3-tooltip__note"><i class="fas fa-clipboard-check"></i> ${escaped}</div>`;
};


function drawBarChart(
  svg: SVGSVGElement,
  rows: DerivedRow[],
  yKey: '_amt' | '_amtLc' | '_amtLcp',
  colorPos: string,
  colorNeg: string,
  showLine: boolean,
  isPercent: boolean,
  avgLine: number | null,
  compact: boolean,
  formatCurrency: (v: number) => string,
) {
  const sel = d3.select(svg);
  sel.selectAll('*').remove();

  const sorted = sortAsc(rows);
  if (sorted.length === 0) return;

  const container = svg.parentElement;
  const width = container?.clientWidth || 800;
  const m = compact ? MARGIN_SM : MARGIN;
  const height = compact ? 300 : 380;

  sel.attr('viewBox', `0 0 ${width} ${height}`)
    .attr('preserveAspectRatio', 'xMidYMid meet');

  const cw = width - m.left - m.right;
  const ch = height - m.top - m.bottom;
  const g = sel.append('g').attr('transform', `translate(${m.left},${m.top})`);

  const yMultiplier = isPercent ? 100 : 1;
  const values = sorted.map((d) => {
    const raw = d[yKey];
    return raw !== null ? raw * yMultiplier : 0;
  });

  const x = d3.scaleBand()
    .domain(sorted.map((d) => String(d.report_year)))
    .range([0, cw])
    .padding(0.35);

  const avgScaled = avgLine !== null ? avgLine * yMultiplier : null;
  const allVals = avgScaled !== null ? [...values, avgScaled] : values;
  const yMin = Math.min(0, d3.min(allVals) ?? 0);
  const yMax = d3.max(allVals) ?? 0;
  const pad = (yMax - yMin) * 0.12 || 1;
  const y = d3.scaleLinear().domain([yMin - pad, yMax + pad]).nice().range([ch, 0]);

  g.append('g')
    .call(d3.axisLeft(y).tickSize(-cw).tickFormat(() => ''))
    .selectAll('line')
    .attr('stroke', '#eef2f7')
    .attr('stroke-dasharray', '3,3');
  g.select('.domain').remove();

  if (yMin < 0) {
    g.append('line')
      .attr('x1', 0)
      .attr('x2', cw)
      .attr('y1', y(0))
      .attr('y2', y(0))
      .attr('stroke', BRAND.mutedGrey)
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,3');
  }

  if (avgScaled !== null) {
    g.append('line')
      .attr('x1', 0)
      .attr('x2', cw)
      .attr('y1', y(avgScaled))
      .attr('y2', y(avgScaled))
      .attr('stroke', BRAND.yellow)
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '8,4')
      .attr('opacity', 0.9);

    g.append('text')
      .attr('x', cw - 4)
      .attr('y', y(avgScaled) - 8)
      .attr('text-anchor', 'end')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '11px')
      .style('font-weight', '700')
      .style('fill', BRAND.yellow)
      .text(`Avg: ${isPercent ? `${avgScaled.toFixed(1)}%` : fmtAxisDollar(avgScaled)}`);
  }

  const tooltip = d3.select('body')
    .selectAll('.tu-d3-tooltip')
    .data([null])
    .join('div')
    .attr('class', 'tu-d3-tooltip')
    .style('position', 'absolute')
    .style('pointer-events', 'none')
    .style('opacity', 0);

  g.selectAll('.tu-bar')
    .data(sorted)
    .join('rect')
    .attr('class', 'tu-bar')
    .attr('x', (d) => x(String(d.report_year)) ?? 0)
    .attr('width', x.bandwidth())
    .attr('y', (d) => {
      const v = (d[yKey] ?? 0) * yMultiplier;
      return v >= 0 ? y(v) : y(0);
    })
    .attr('height', (d) => {
      const v = (d[yKey] ?? 0) * yMultiplier;
      return Math.abs(y(0) - y(v));
    })
    .attr('rx', 4)
    .attr('fill', (d) => ((d[yKey] ?? 0) >= 0 ? colorPos : colorNeg))
    .attr('opacity', 0.85)
    .on('mouseover', function (event, d) {
      d3.select(this)
        .attr('opacity', 1)
        .attr('stroke', BRAND.black)
        .attr('stroke-width', 1.5);
      const raw = d[yKey];
      const label = isPercent ? fmtPct(raw) : formatCurrency(raw ?? 0);
      tooltip
        .style('opacity', 1)
        .html(`<div class="tu-d3-tooltip__inner"><strong>${d.report_year}</strong><br/>${isPercent ? 'Change' : yKey === '_amt' ? 'Revenue' : 'Change'}: ${label}${noteHtml(d)}</div>`)
        .style('left', event.pageX + 14 + 'px')
        .style('top', event.pageY - 28 + 'px');
    })
    .on('mousemove', function (event) {
      tooltip
        .style('left', event.pageX + 14 + 'px')
        .style('top', event.pageY - 28 + 'px');
    })
    .on('mouseout', function () {
      d3.select(this)
        .attr('opacity', 0.85)
        .attr('stroke', 'none');
      tooltip.style('opacity', 0);
    });

  if (showLine && sorted.length > 1) {
    const line = d3.line<DerivedRow>()
      .x((d) => (x(String(d.report_year)) ?? 0) + x.bandwidth() / 2)
      .y((d) => y((d[yKey] ?? 0) * yMultiplier))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(sorted)
      .attr('fill', 'none')
      .attr('stroke', BRAND.tealDark)
      .attr('stroke-width', 3)
      .attr('d', line);

    g.selectAll('.tu-dot')
      .data(sorted)
      .join('circle')
      .attr('class', 'tu-dot')
      .attr('cx', (d) => (x(String(d.report_year)) ?? 0) + x.bandwidth() / 2)
      .attr('cy', (d) => y((d[yKey] ?? 0) * yMultiplier))
      .attr('r', 5)
      .attr('fill', BRAND.tealDark)
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2);
  }

  g.append('g')
    .attr('transform', `translate(0,${ch})`)
    .call(d3.axisBottom(x).tickSizeOuter(0))
    .selectAll('text')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', compact ? '11px' : '13px')
    .style('font-weight', '600')
    .style('fill', BRAND.mutedGrey);

  g.append('g')
    .call(
      d3.axisLeft(y)
        .ticks(compact ? 5 : 6)
        .tickFormat((d) => {
          const v = d as number;
          return isPercent ? `${v.toFixed(0)}%` : fmtAxisDollar(v);
        }),
    )
    .selectAll('text')
    .style('font-family', "'Outfit', Arial, sans-serif")
    .style('font-size', '12px')
    .style('fill', BRAND.grey);

  g.selectAll('.domain').attr('stroke', '#eef2f7');
}


function drawPerCapitaChart(
  svg: SVGSVGElement,
  rows: { report_year: number; perCapita: number }[],
  formatCurrency: (v: number) => string,
) {
  const sel = d3.select(svg);
  sel.selectAll('*').remove();

  const sorted = [...rows].sort((a, b) => a.report_year - b.report_year);
  if (sorted.length === 0) return;

  const container = svg.parentElement;
  const width = container?.clientWidth || 600;
  const height = 300;
  const m = MARGIN_SM;

  sel.attr('viewBox', `0 0 ${width} ${height}`)
    .attr('preserveAspectRatio', 'xMidYMid meet');

  const cw = width - m.left - m.right;
  const ch = height - m.top - m.bottom;
  const g = sel.append('g').attr('transform', `translate(${m.left},${m.top})`);

  const x = d3.scaleBand()
    .domain(sorted.map((d) => String(d.report_year)))
    .range([0, cw])
    .padding(0.35);

  const yMax = d3.max(sorted, (d) => d.perCapita) ?? 0;
  const pad = yMax * 0.12 || 1;
  const y = d3.scaleLinear().domain([0, yMax + pad]).nice().range([ch, 0]);

  g.append('g')
    .call(d3.axisLeft(y).tickSize(-cw).tickFormat(() => ''))
    .selectAll('line')
    .attr('stroke', '#eef2f7')
    .attr('stroke-dasharray', '3,3');
  g.select('.domain').remove();

  const tooltip = d3.select('body')
    .selectAll('.tu-d3-tooltip')
    .data([null])
    .join('div')
    .attr('class', 'tu-d3-tooltip')
    .style('position', 'absolute')
    .style('pointer-events', 'none')
    .style('opacity', 0);

  g.selectAll('.tu-bar')
    .data(sorted)
    .join('rect')
    .attr('class', 'tu-bar')
    .attr('x', (d) => x(String(d.report_year)) ?? 0)
    .attr('width', x.bandwidth())
    .attr('y', (d) => y(d.perCapita))
    .attr('height', (d) => ch - y(d.perCapita))
    .attr('rx', 4)
    .attr('fill', BRAND.teal)
    .attr('opacity', 0.85)
    .on('mouseover', function (event, d) {
      d3.select(this)
        .attr('opacity', 1)
        .attr('stroke', BRAND.black)
        .attr('stroke-width', 1.5);
      tooltip
        .style('opacity', 1)
        .html(`<div class="tu-d3-tooltip__inner"><strong>${d.report_year}</strong><br/>Per Capita: ${formatCurrency(d.perCapita)}</div>`)
        .style('left', event.pageX + 14 + 'px')
        .style('top', event.pageY - 28 + 'px');
    })
    .on('mousemove', function (event) {
      tooltip
        .style('left', event.pageX + 14 + 'px')
        .style('top', event.pageY - 28 + 'px');
    })
    .on('mouseout', function () {
      d3.select(this)
        .attr('opacity', 0.85)
        .attr('stroke', 'none');
      tooltip.style('opacity', 0);
    });

  if (sorted.length > 1) {
    const line = d3.line<typeof sorted[0]>()
      .x((d) => (x(String(d.report_year)) ?? 0) + x.bandwidth() / 2)
      .y((d) => y(d.perCapita))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(sorted)
      .attr('fill', 'none')
      .attr('stroke', BRAND.tealDark)
      .attr('stroke-width', 3)
      .attr('d', line);

    g.selectAll('.tu-dot')
      .data(sorted)
      .join('circle')
      .attr('class', 'tu-dot')
      .attr('cx', (d) => (x(String(d.report_year)) ?? 0) + x.bandwidth() / 2)
      .attr('cy', (d) => y(d.perCapita))
      .attr('r', 5)
      .attr('fill', BRAND.tealDark)
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2);
  }

  g.append('g')
    .attr('transform', `translate(0,${ch})`)
    .call(d3.axisBottom(x).tickSizeOuter(0))
    .selectAll('text')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', '11px')
    .style('font-weight', '600')
    .style('fill', BRAND.mutedGrey);

  g.append('g')
    .call(
      d3.axisLeft(y)
        .ticks(5)
        .tickFormat((d) => fmtAxisDollar(d as number)),
    )
    .selectAll('text')
    .style('font-family', "'Outfit', Arial, sans-serif")
    .style('font-size', '11px')
    .style('fill', BRAND.grey);

  g.selectAll('.domain').attr('stroke', '#eef2f7');
}


function drawSlopeChart(
  svg: SVGSVGElement,
  rows: DerivedRow[],
  formatCurrency: (v: number) => string,
) {
  const sel = d3.select(svg);
  sel.selectAll('*').remove();
  const sorted = sortAsc(rows.filter((d) => d._amtL6 !== null));
  if (sorted.length === 0) return;
  const container = svg.parentElement;
  const width = container?.clientWidth || 800;
  const height = 380;
  const m = { top: 32, right: 140, bottom: 28, left: 140 };
  sel.attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMidYMid meet');
  const cw = width - m.left - m.right;
  const ch = height - m.top - m.bottom;
  const g = sel.append('g').attr('transform', `translate(${m.left},${m.top})`);
  const allVals = sorted.flatMap((d) => [d._amt, d._amtL6 ?? 0]);
  const yMin = d3.min(allVals) ?? 0;
  const yMax = d3.max(allVals) ?? 0;
  const pad = (yMax - yMin) * 0.15 || 1;
  const y = d3.scaleLinear().domain([yMin - pad, yMax + pad]).nice().range([ch, 0]);
  const xLeft = 0;
  const xRight = cw;

  g.append('g')
    .call(d3.axisLeft(y).tickSize(-cw).tickFormat(() => ''))
    .selectAll('line')
    .attr('stroke', '#eef2f7')
    .attr('stroke-dasharray', '3,3');
  g.select('.domain').remove();
  g.append('text')
    .attr('x', xLeft)
    .attr('y', -14)
    .attr('text-anchor', 'middle')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', '13px')
    .style('font-weight', '700')
    .style('fill', BRAND.mutedGrey)
    .text('6 Years Prior');
  g.append('text')
    .attr('x', xRight)
    .attr('y', -14)
    .attr('text-anchor', 'middle')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', '13px')
    .style('font-weight', '700')
    .style('fill', BRAND.mutedGrey)
    .text('Current Year');

  const tooltip = d3.select('body')
    .selectAll('.tu-d3-tooltip')
    .data([null])
    .join('div')
    .attr('class', 'tu-d3-tooltip')
    .style('position', 'absolute')
    .style('pointer-events', 'none')
    .style('opacity', 0);

  sorted.forEach((d) => {
    const priorVal = d._amtL6 ?? 0;
    const currentVal = d._amt;
    const change = d._amtLc6 ?? 0;
    const lineColor = change >= 0 ? BRAND.green : BRAND.red;
    const yPrior = y(priorVal);
    const yCurrent = y(currentVal);

    g.append('line').attr('x1', xLeft).attr('y1', yPrior).attr('x2', xRight).attr('y2', yCurrent)
      .attr('stroke', 'transparent').attr('stroke-width', 16).style('cursor', 'pointer')
      .on('mouseover', function (event) {
        tooltip.style('opacity', 1)
          .html(`<div class="tu-d3-tooltip__inner"><strong>${d.report_year - 6} to ${d.report_year}</strong><br/>From: ${formatCurrency(priorVal)}<br/>To: ${formatCurrency(currentVal)}<br/>Change: ${change >= 0 ? '+' : ''}${formatCurrency(change)} (${fmtPct(d._amtLcp6)})${noteHtml(d)}</div>`)
          .style('left', event.pageX + 14 + 'px').style('top', event.pageY - 28 + 'px');
      })
      .on('mousemove', function (event) { tooltip.style('left', event.pageX + 14 + 'px')
        .style('top', event.pageY - 28 + 'px'); })
      .on('mouseout', function () { tooltip.style('opacity', 0); });

    g.append('line')
      .attr('x1', xLeft)
      .attr('y1', yPrior)
      .attr('x2', xRight)
      .attr('y2', yCurrent)
      .attr('stroke', lineColor)
      .attr('stroke-width', 3)
      .attr('stroke-linecap', 'round')
      .attr('opacity', 0.85)
      .style('pointer-events', 'none');
    g.append('circle')
      .attr('cx', xLeft)
      .attr('cy', yPrior)
      .attr('r', 6)
      .attr('fill', '#CBD5E1')
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2.5);
    g.append('circle')
      .attr('cx', xRight)
      .attr('cy', yCurrent)
      .attr('r', 6)
      .attr('fill', lineColor)
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2.5);
    g.append('text')
      .attr('x', xLeft - 12)
      .attr('y', yPrior)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '12px')
      .style('fill', BRAND.mutedGrey)
      .text(`${d.report_year - 6}: ${fmtShortDollar(priorVal)}`);
    g.append('text')
      .attr('x', xRight + 12)
      .attr('y', yCurrent)
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '12px')
      .style('font-weight', '700')
      .style('fill', lineColor)
      .text(`${d.report_year}: ${fmtShortDollar(currentVal)}`);
    const midX = cw / 2;
    const midY = (yPrior + yCurrent) / 2;
    g.append('text')
      .attr('x', midX)
      .attr('y', midY - 12)
      .attr('text-anchor', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '11px')
      .style('font-weight', '700')
      .style('fill', lineColor)
      .text(`${change >= 0 ? '+' : ''}${fmtShortDollar(change)}`);
  });
}


function drawSixYrPctChart(
  svg: SVGSVGElement,
  rows: DerivedRow[],
  formatCurrency: (v: number) => string,
) {
  const sel = d3.select(svg);
  sel.selectAll('*').remove();
  const sorted = sortAsc(rows.filter((d) => d._amtLcp6 !== null && d._amtL6 !== null));
  if (sorted.length === 0) return;
  const container = svg.parentElement;
  const width = container?.clientWidth || 800;
  const height = 380;
  const m = { top: 32, right: 140, bottom: 28, left: 140 };
  sel.attr('viewBox', `0 0 ${width} ${height}`).attr('preserveAspectRatio', 'xMidYMid meet');
  const cw = width - m.left - m.right;
  const ch = height - m.top - m.bottom;
  const g = sel.append('g').attr('transform', `translate(${m.left},${m.top})`);
  const allVals = sorted.flatMap((d) => [d._amt, d._amtL6 ?? 0]);
  const yMin = d3.min(allVals) ?? 0;
  const yMax = d3.max(allVals) ?? 0;
  const pad = (yMax - yMin) * 0.15 || 1;
  const y = d3.scaleLinear().domain([yMin - pad, yMax + pad]).nice().range([ch, 0]);
  const xLeft = 0;
  const xRight = cw;

  g.append('g')
    .call(d3.axisLeft(y).tickSize(-cw).tickFormat(() => ''))
    .selectAll('line')
    .attr('stroke', '#eef2f7')
    .attr('stroke-dasharray', '3,3');
  g.select('.domain').remove();
  g.append('text')
    .attr('x', xLeft)
    .attr('y', -14)
    .attr('text-anchor', 'middle')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', '13px')
    .style('font-weight', '700')
    .style('fill', BRAND.mutedGrey)
    .text('6 Years Prior');
  g.append('text')
    .attr('x', xRight)
    .attr('y', -14)
    .attr('text-anchor', 'middle')
    .style('font-family', "'Barlow Condensed', Arial, sans-serif")
    .style('font-size', '13px')
    .style('font-weight', '700')
    .style('fill', BRAND.mutedGrey)
    .text('Current Year');

  const tooltip = d3.select('body')
    .selectAll('.tu-d3-tooltip')
    .data([null])
    .join('div')
    .attr('class', 'tu-d3-tooltip')
    .style('position', 'absolute')
    .style('pointer-events', 'none')
    .style('opacity', 0);

  sorted.forEach((d) => {
    const priorVal = d._amtL6 ?? 0;
    const currentVal = d._amt;
    const pctChange = d._amtLcp6 ?? 0;
    const lineColor = pctChange >= 0 ? BRAND.green : BRAND.red;
    const yPrior = y(priorVal);
    const yCurrent = y(currentVal);

    g.append('line').attr('x1', xLeft).attr('y1', yPrior).attr('x2', xRight).attr('y2', yCurrent)
      .attr('stroke', 'transparent').attr('stroke-width', 16).style('cursor', 'pointer')
      .on('mouseover', function (event) {
        tooltip.style('opacity', 1)
          .html(`<div class="tu-d3-tooltip__inner"><strong>${d.report_year - 6} to ${d.report_year}</strong><br/>From: ${formatCurrency(priorVal)}<br/>To: ${formatCurrency(currentVal)}<br/>Change: ${pctChange >= 0 ? '+' : ''}${fmtPct(pctChange)}${noteHtml(d)}</div>`)
          .style('left', event.pageX + 14 + 'px').style('top', event.pageY - 28 + 'px');
      })
      .on('mousemove', function (event) { tooltip.style('left', event.pageX + 14 + 'px')
        .style('top', event.pageY - 28 + 'px'); })
      .on('mouseout', function () { tooltip.style('opacity', 0); });

    g.append('line')
      .attr('x1', xLeft)
      .attr('y1', yPrior)
      .attr('x2', xRight)
      .attr('y2', yCurrent)
      .attr('stroke', lineColor)
      .attr('stroke-width', 3)
      .attr('stroke-linecap', 'round')
      .attr('opacity', 0.85)
      .style('pointer-events', 'none');
    g.append('circle')
      .attr('cx', xLeft)
      .attr('cy', yPrior)
      .attr('r', 6)
      .attr('fill', '#CBD5E1')
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2.5);
    g.append('circle')
      .attr('cx', xRight)
      .attr('cy', yCurrent)
      .attr('r', 6)
      .attr('fill', lineColor)
      .attr('stroke', BRAND.white)
      .attr('stroke-width', 2.5);
    g.append('text')
      .attr('x', xLeft - 12)
      .attr('y', yPrior)
      .attr('text-anchor', 'end')
      .attr('dominant-baseline', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '12px')
      .style('fill', BRAND.mutedGrey)
      .text(`${d.report_year - 6}: ${fmtShortDollar(priorVal)}`);
    g.append('text')
      .attr('x', xRight + 12)
      .attr('y', yCurrent)
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '12px')
      .style('font-weight', '700')
      .style('fill', lineColor)
      .text(`${d.report_year}: ${fmtShortDollar(currentVal)}`);
    const midX = cw / 2;
    const midY = (yPrior + yCurrent) / 2;
    g.append('text')
      .attr('x', midX)
      .attr('y', midY - 12)
      .attr('text-anchor', 'middle')
      .style('font-family', "'Outfit', Arial, sans-serif")
      .style('font-size', '12px')
      .style('font-weight', '700')
      .style('fill', lineColor)
      .text(`${pctChange >= 0 ? '+' : ''}${fmtPct(pctChange)}`);
  });
}


const NoteIndicator: React.FC<{ note: string }> = ({ note }) => (
  <span className="tu-revTrend__noteIcon" title={note}>
    <i className="fas fa-clipboard-check" />
  </span>
);

const ChartSection: React.FC<{
  title: string;
  icon: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}> = ({ title, icon, description, children, className }) => (
  <section className={`tu-card tu-revTrend__section ${className || ''}`}>
    <header className="tu-card__header">
      <div className="tu-cardHeaderRow">
        <span className="tu-cardIcon" aria-hidden="true"><i className={icon} /></span>
        <h3 className="tu-card__title">{title}</h3>
      </div>
    </header>
    <div className="tu-card__body">
      {description && <p className="tu-revTrend__sectionDesc">{description}</p>}
      {children}
    </div>
  </section>
);

const CompareToggle: React.FC<{
  value: 'yoy' | '6yr';
  onChange: (v: 'yoy' | '6yr') => void;
}> = ({ value, onChange }) => (
  <div className="tu-revTrend__segmented">
    <button
      type="button"
      className={`tu-revTrend__segBtn ${value === 'yoy' ? 'tu-revTrend__segBtn--active' : ''}`}
      onClick={() => onChange('yoy')}
    >
      Year-over-Year
    </button>
    <button
      type="button"
      className={`tu-revTrend__segBtn ${value === '6yr' ? 'tu-revTrend__segBtn--active' : ''}`}
      onClick={() => onChange('6yr')}
    >
      6-Year Comparison
    </button>
  </div>
);


const EntityTotalRevenueOverview: React.FC<EntityTotalRevenueOverviewProps> = ({
  data, loading, parentEntityName, parentEntityId, formatCurrency, formatEntityString, activeTab, entityPopulationData,
}) => {
  const [showHeaderInfo, setShowHeaderInfo] = useState(false);
  const [adjustInflation, setAdjustInflation] = useState(false);
  const [compareMode, setCompareMode] = useState<'yoy' | '6yr'>('yoy');

  const totalRevRef = useRef<SVGSVGElement | null>(null);
  const dollarBarRef = useRef<SVGSVGElement | null>(null);
  const pctBarRef = useRef<SVGSVGElement | null>(null);
  const perCapitaBarRef = useRef<SVGSVGElement | null>(null);

  const derived: DerivedRow[] = useMemo(() => data
    .filter((d) => d.report_year != null)
    .map((d) => {
      const rawAmt = adjustInflation
        ? (safeNum(d.net_amount_inflation) ?? safeNum(d.net_amount) ?? 0)
        : (safeNum(d.net_amount) ?? 0);
      const lag    = adjustInflation ? safeNum(d.net_amount_inflation_l)   : safeNum(d.net_amount_l);
      const lagChg = adjustInflation ? safeNum(d.net_amount_inflation_lc)  : safeNum(d.net_amount_lc);
      const lagPct = adjustInflation ? safeNum(d.net_amount_inflation_lcp) : safeNum(d.net_amount_lcp);
      const amt = rawAmt;
      const amtLcp = lagPct !== null
        ? lagPct
        : (lag !== null && lagChg !== null && lag !== 0) ? lagChg / lag : null;

      return {
        ...d,
        _amt: amt,
        _amtL: lag,
        _amtLc: lagChg,
        _amtLcp: amtLcp,
        _amtL6:   adjustInflation ? safeNum(d.net_amount_inflation_l6)   : safeNum(d.net_amount_l6),
        _amtLc6:  adjustInflation ? safeNum(d.net_amount_inflation_lc6)  : safeNum(d.net_amount_lc6),
        _amtLcp6: adjustInflation ? safeNum(d.net_amount_inflation_lcp6) : safeNum(d.net_amount_lcp6),
      };
    }), [data, adjustInflation]);

  const chartData = useMemo(() => sortAsc(derived), [derived]);
  const tableData = useMemo(() => sortDesc(derived), [derived]);
  const changeRows = useMemo(() => derived.filter((d) => d._amtLc !== null), [derived]);
  const pctRows = useMemo(() => derived.filter((d) => d._amtLcp !== null), [derived]);
  const sixYrRows = useMemo(() => derived.filter((d) => d._amtLc6 !== null), [derived]);

  const avgRevenue = useMemo(() => {
    if (derived.length === 0) return null;
    return derived.reduce((s, d) => s + d._amt, 0) / derived.length;
  }, [derived]);

  const avgYoyChange = useMemo(() => {
    if (changeRows.length === 0) return null;
    return changeRows.reduce((s, d) => s + (d._amtLc ?? 0), 0) / changeRows.length;
  }, [changeRows]);

  const cagr = useMemo(() => {
    const sorted = chartData;
    if (sorted.length < 2) return null;
    const first = sorted[0]._amt;
    const last = sorted[sorted.length - 1]._amt;
    if (first === 0 || first < 0 || last < 0) return null;
    const n = sorted[sorted.length - 1].report_year - sorted[0].report_year;
    if (n <= 0) return null;
    return Math.pow(last / first, 1 / n) - 1;
  }, [chartData]);

  const biggestIncrease = useMemo(() => {
    if (changeRows.length === 0) return null;
    return changeRows.reduce((best, d) => (d._amtLc! > best._amtLc!) ? d : best, changeRows[0]);
  }, [changeRows]);

  const biggestDecrease = useMemo(() => {
    if (changeRows.length === 0) return null;
    const worst = changeRows.reduce((best, d) => (d._amtLc! < best._amtLc!) ? d : best, changeRows[0]);
    return worst._amtLc! < 0 ? worst : null;
  }, [changeRows]);

  const volatility = useMemo(() => {
    if (pctRows.length < 2) return null;
    const pctValues = pctRows.map((d) => (d._amtLcp ?? 0) * 100);
    const mean = pctValues.reduce((s, v) => s + v, 0) / pctValues.length;
    const variance = pctValues.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / pctValues.length;
    const stdDev = Math.sqrt(variance);
    let label: 'Low' | 'Moderate' | 'High' = 'Low';
    if (stdDev >= 25) label = 'High';
    else if (stdDev >= 10) label = 'Moderate';
    return { stdDev, label };
  }, [pctRows]);

  const popMap = useMemo(() => {
    if (!entityPopulationData || entityPopulationData.length === 0) return new Map<number, number>();
    const m = new Map<number, number>();
    entityPopulationData.forEach((p) => {
      if (p.census_year && p.census_population > 0) m.set(p.census_year, p.census_population);
    });
    return m;
  }, [entityPopulationData]);

  interface PerCapitaRow { report_year: number; _amt: number; population: number; perCapita: number; }
  const perCapitaData: PerCapitaRow[] = useMemo(() => {
    if (popMap.size === 0 || derived.length === 0) return [];
    const censusYears = Array.from(popMap.keys()).sort((a, b) => a - b);
    const findClosestPop = (year: number): number | null => {
      let closest = censusYears[0];
      let minDiff = Math.abs(year - closest);
      for (const cy of censusYears) {
        const diff = Math.abs(year - cy);
        if (diff < minDiff) { closest = cy; minDiff = diff; }
      }
      return minDiff <= 5 ? (popMap.get(closest) ?? null) : null;
    };
    return sortAsc(derived).reduce<PerCapitaRow[]>((acc, d) => {
      const pop = findClosestPop(d.report_year);
      if (pop && pop > 0) acc.push({ report_year: d.report_year, _amt: d._amt, population: pop, perCapita: d._amt / pop });
      return acc;
    }, []);
  }, [derived, popMap]);

  const latestPerCapita = perCapitaData.length > 0 ? perCapitaData[perCapitaData.length - 1] : null;
  const latestYear = derived.length > 0 ? sortDesc(derived)[0] : null;
  const inflationLabel = adjustInflation ? ' (Inflation-Adjusted)' : '';

  useEffect(() => {
    if (activeTab !== 'revenueTrends' || derived.length === 0) return;
    const frame = requestAnimationFrame(() => {
      if (totalRevRef.current) drawBarChart(totalRevRef.current, derived, '_amt', BRAND.teal, BRAND.red, true, false, avgRevenue, false, formatCurrency);
      if (dollarBarRef.current) {
        if (compareMode === 'yoy') drawBarChart(dollarBarRef.current, changeRows, '_amtLc', BRAND.green, BRAND.red, true, false, null, true, formatCurrency);
        else drawSlopeChart(dollarBarRef.current, sixYrRows, formatCurrency);
      }
      if (pctBarRef.current) {
        if (compareMode === 'yoy') drawBarChart(pctBarRef.current, pctRows, '_amtLcp', BRAND.green, BRAND.red, true, true, null, true, formatCurrency);
        else drawSixYrPctChart(pctBarRef.current, sixYrRows, formatCurrency);
      }
      if (perCapitaBarRef.current && perCapitaData.length > 0) drawPerCapitaChart(perCapitaBarRef.current, perCapitaData, formatCurrency);
    });
    const handleResize = () => {
      if (totalRevRef.current) drawBarChart(totalRevRef.current, derived, '_amt', BRAND.teal, BRAND.red, true, false, avgRevenue, false, formatCurrency);
      if (dollarBarRef.current) { if (compareMode === 'yoy') drawBarChart(dollarBarRef.current, changeRows, '_amtLc', BRAND.green, BRAND.red, true, false, null, true, formatCurrency);
      else drawSlopeChart(dollarBarRef.current, sixYrRows, formatCurrency);
      }
      if (pctBarRef.current) { if (compareMode === 'yoy') drawBarChart(pctBarRef.current, pctRows, '_amtLcp', BRAND.green, BRAND.red, true, true, null, true, formatCurrency);
      else drawSixYrPctChart(pctBarRef.current, sixYrRows, formatCurrency);
      }
      if (perCapitaBarRef.current && perCapitaData.length > 0) drawPerCapitaChart(perCapitaBarRef.current, perCapitaData, formatCurrency);
    };
    window.addEventListener('resize', handleResize);
    return () => { cancelAnimationFrame(frame); window.removeEventListener('resize', handleResize); };
  }, [derived, changeRows, pctRows, sixYrRows, avgRevenue, perCapitaData, activeTab, formatCurrency, compareMode]);

  return (
    <Row className="mb-2">
      <Col xs={12}>
        <Card bg="secondary" text="white" style={{ border: 'none', borderRadius: '0' }}>
          <Card.Body style={{ padding: 0 }}>
            <div className="tu-tabHeader">
              <div className="tu-tabHeader__titleRow">
                <h1 className="tu-tabHeader__title">Revenue Trends</h1>
                <button type="button" className="tu-tabHeader__infoBtn" onClick={() => setShowHeaderInfo(!showHeaderInfo)} aria-label="Revenue Trends info">
                  <i className="fas fa-info" />
                </button>
              </div>
              <Collapse in={showHeaderInfo}>
                <div className="tu-tabHeader__desc">
                  This page shows{parentEntityName ? ` ${parentEntityName}'s` : " an entity's"} total
                  revenue over time as reported in the Annual Comprehensive Financial Report (ACFR),
                  not Transparent Utah general ledger transactions. Includes year-over-year dollar and
                  percentage changes, growth metrics, and per capita analysis when population data is
                  available. Toggle inflation adjustment to view values in constant 2019 dollars.
                </div>
              </Collapse>
            </div>
          </Card.Body>
        </Card>

        {loading ? (
          <Card className="mb-4">
            <div className="p-5 text-center">
              <div className="spinner-border" role="status" style={{ color: BRAND.teal }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3" style={{ color: BRAND.grey }}>Loading revenue data...</p>
            </div>
          </Card>
        ) : data.length === 0 || derived.length === 0 ? (
          <Card className="mb-4">
            <div className="p-5 text-center">
              <div className="alert alert-light shadow-sm">No revenue trend data available for this entity.</div>
            </div>
          </Card>
        ) : (
          <div className="tu-entityOverview">

            <div className="tu-revTrend__toggleRow">
              <div className="tu-revTrend__toggle">
                <label className="tu-revTrend__toggleLabel" htmlFor="inflation-toggle">
                  <i className="fas fa-balance-scale" style={{ marginRight: 8, color: BRAND.teal }} />
                  Adjust for Inflation (2019 Dollars)
                </label>
                <div
                  className={`tu-revTrend__switch ${adjustInflation ? 'tu-revTrend__switch--on' : ''}`}
                  onClick={() => setAdjustInflation(!adjustInflation)}
                  role="switch"
                  aria-checked={adjustInflation}
                  tabIndex={0}
                  id="inflation-toggle"
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAdjustInflation(!adjustInflation); } }}
                >
                  <div className="tu-revTrend__switchThumb" />
                </div>
              </div>
              {adjustInflation && <span className="tu-revTrend__toggleHint">All values adjusted to 2019 dollars using CPI-U</span>}
            </div>

            <div className="tu-revTrend__kpiRow">
              <div className="tu-revTrend__kpi">
                <div className="tu-revTrend__kpiIcon"><i className="fas fa-calendar-alt" /></div>
                <div><div className="tu-revTrend__kpiValue">{latestYear ? latestYear.report_year : '—'}</div><div className="tu-revTrend__kpiLabel">Latest Year</div></div>
              </div>
              <div className="tu-revTrend__kpi">
                <div className="tu-revTrend__kpiIcon"><i className="fas fa-coins" /></div>
                <div><div className="tu-revTrend__kpiValue">{latestYear ? fmtShortDollar(latestYear._amt) : '—'}</div><div className="tu-revTrend__kpiLabel">Latest Revenue</div></div>
              </div>
              <div className="tu-revTrend__kpi">
                <div className="tu-revTrend__kpiIcon"><i className="fas fa-chart-line" /></div>
                <div>
                  <div className="tu-revTrend__kpiValue" style={{
                     color: latestYear && latestYear._amtLc !== null && latestYear._amtLc > 0 ? BRAND.green : latestYear && latestYear._amtLc !== null && latestYear._amtLc < 0 ? BRAND.red : BRAND.mutedGrey }}>
                    {latestYear && latestYear._amtLc !== null ? `${latestYear._amtLc >= 0 ? '+' : ''}${fmtShortDollar(latestYear._amtLc)}` : '—'}
                  </div>
                  <div className="tu-revTrend__kpiLabel">Last Year Change</div>
                </div>
              </div>
              <div className="tu-revTrend__kpi">
                <div className="tu-revTrend__kpiIcon"><i className="fas fa-percentage" /></div>
                <div>
                  <div className="tu-revTrend__kpiValue" style={{
                     color: latestYear && latestYear._amtLcp !== null && latestYear._amtLcp > 0 ? BRAND.green : latestYear && latestYear._amtLcp !== null && latestYear._amtLcp < 0 ? BRAND.red : BRAND.mutedGrey }}>
                    {latestYear && latestYear._amtLcp !== null ? `${latestYear._amtLcp >= 0 ? '+' : ''}${fmtPct(latestYear._amtLcp)}` : '—'}
                  </div>
                  <div className="tu-revTrend__kpiLabel">% Change (Prior Year)</div>
                </div>
              </div>
              <div className="tu-revTrend__kpi">
                <div className="tu-revTrend__kpiIcon"><i className="fas fa-history" /></div>
                <div><div className="tu-revTrend__kpiValue">{derived.length}</div><div className="tu-revTrend__kpiLabel">Years of Data</div></div>
              </div>
              {cagr !== null && (
                <div className="tu-revTrend__kpi">
                  <div className="tu-revTrend__kpiIcon"><i className="fas fa-seedling" /></div>
                  <div>
                    <div className="tu-revTrend__kpiValue" style={{
                       color: cagr > 0 ? BRAND.green : cagr < 0 ? BRAND.red : BRAND.mutedGrey }}>{`${cagr >= 0 ? '+' : ''}${(cagr * 100).toFixed(2)}%`}</div>
                    <div className="tu-revTrend__kpiLabel">Avg Annual Growth (CAGR)</div>
                  </div>
                </div>
              )}
            </div>

            <div className="tu-revTrend__kpiRow">
              {avgYoyChange !== null && (
                <div className="tu-revTrend__kpi">
                  <div className="tu-revTrend__kpiIcon"><i className="fas fa-arrows-alt-v" /></div>
                  <div>
                    <div className="tu-revTrend__kpiValue" style={{
                       color: avgYoyChange > 0 ? BRAND.green : avgYoyChange < 0 ? BRAND.red : BRAND.mutedGrey }}>{`${avgYoyChange >= 0 ? '+' : ''}${fmtShortDollar(avgYoyChange)}`}</div>
                    <div className="tu-revTrend__kpiLabel">Avg Year over Year Change</div>
                  </div>
                </div>
              )}
              {volatility !== null && (
                <div className="tu-revTrend__kpi">
                  <div className="tu-revTrend__kpiIcon"><i className="fas fa-wave-square" /></div>
                  <div>
                    <div className="tu-revTrend__kpiValue"><span className={`tu-revTrend__volBadge tu-revTrend__volBadge--${volatility.label.toLowerCase()}`}>{volatility.label}</span></div>
                    <div className="tu-revTrend__kpiLabel">Revenue Volatility ({volatility.stdDev.toFixed(1)}% St Deviation)</div>
                  </div>
                </div>
              )}
              {latestPerCapita && (
                <div className="tu-revTrend__kpi">
                  <div className="tu-revTrend__kpiIcon" style={{ background: 'rgba(46, 139, 139, 0.10)', borderColor: 'rgba(46, 139, 139, 0.18)' }}>
                    <i className="fas fa-user-friends" style={{ color: BRAND.teal }} />
                  </div>
                  <div>
                    <div className="tu-revTrend__kpiValue" style={{ color: BRAND.tealDark }}>{formatCurrency(latestPerCapita.perCapita)}</div>
                    <div className="tu-revTrend__kpiLabel">Revenue Per Capita ({latestPerCapita.report_year})</div>
                  </div>
                </div>
              )}
              {biggestIncrease && (
                <div className="tu-revTrend__callout tu-revTrend__callout--up">
                  <div className="tu-revTrend__calloutIcon"><i className="fas fa-arrow-up" /></div>
                  <div>
                    <div className="tu-revTrend__calloutTitle">Largest Increase</div>
                    <div className="tu-revTrend__calloutValue">{biggestIncrease.report_year} — +{fmtShortDollar(biggestIncrease._amtLc!)}
                      <span className="tu-revTrend__calloutPct">({biggestIncrease._amtLcp !== null ? `+${fmtPct(biggestIncrease._amtLcp)}` : '—'})</span></div>
                    <div className="tu-revTrend__calloutSub">Revenue went from {fmtShortDollar(biggestIncrease._amtL ?? 0)} to {fmtShortDollar(biggestIncrease._amt)}</div>
                  </div>
                </div>
              )}
              {biggestDecrease && (
                <div className="tu-revTrend__callout tu-revTrend__callout--down">
                  <div className="tu-revTrend__calloutIcon"><i className="fas fa-arrow-down" /></div>
                  <div>
                    <div className="tu-revTrend__calloutTitle">Largest Decrease</div>
                    <div className="tu-revTrend__calloutValue">{biggestDecrease.report_year} — {fmtShortDollar(biggestDecrease._amtLc!)}
                      <span className="tu-revTrend__calloutPct">({biggestDecrease._amtLcp !== null ? fmtPct(biggestDecrease._amtLcp) : '—'})</span></div>
                    <div className="tu-revTrend__calloutSub">Revenue went from {fmtShortDollar(biggestDecrease._amtL ?? 0)} to {fmtShortDollar(biggestDecrease._amt)}</div>
                  </div>
                </div>
              )}
            </div>

            <ChartSection title={`Total Revenue by Year${inflationLabel}`} icon="fas fa-coins" description="Total annual revenue. Bars = each fiscal year, dark line = trend, dashed yellow = average.">
              <div className="tu-revTrend__chartWrap"><svg ref={totalRevRef} className="tu-revTrend__svg" /></div>
              <div className="tu-revTrend__legend">
                <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch" style={{ background: BRAND.teal }} />Revenue</span>
                <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch tu-revTrend__legendSwatch--line" style={{ background: BRAND.tealDark }} />Trend</span>
                <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch tu-revTrend__legendSwatch--dashed" />Average</span>
              </div>
              <div className="tu-revTrend__tableSpace"><div className="tu-revTrend__tableWrap"><table className="tu-revTrend__table">
                    <thead>
                  <tr>
                    <th className="tu-col-year">Year</th>
                    <th>Entity</th>
                    <th style={{ textAlign: 'right' }}>Total Revenue</th>
                    <th style={{ textAlign: 'center' }}>Note</th>
                  </tr>
                </thead>
                <tbody>{tableData.map((d) => (
                  <React.Fragment key={`rev-${d.report_year}`}>
                    <tr>
                      <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                      <td>{d.sf_name}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                      <td style={{ textAlign: 'center' }}>{hasNote(d) && <NoteIndicator note={d.public_explanation} />}</td>
                    </tr>
                    {hasNote(d) && (
                      <tr className="tu-revTrend__noteRow"><td colSpan={4}><div className="tu-revTrend__noteCell"><i className="fas fa-clipboard-check" /> {d.public_explanation}</div></td></tr>
                    )}
                  </React.Fragment>
                ))}</tbody>
              </table></div></div>
            </ChartSection>

            <div className="tu-revTrend__compareHeader">
              <div>
                <h3 className="tu-revTrend__compareTitle"><i className="fas fa-exchange-alt" style={{ marginRight: 10, color: BRAND.teal }} />Revenue Changes{inflationLabel}</h3>
                <p className="tu-revTrend__compareDesc">{compareMode === 'yoy' ? 'How much revenue went up or down each year compared to the year before.' : 'How has revenue changed over a longer time period? Each year is compared to 6 years earlier.'}</p>
              </div>
              {sixYrRows.length > 0 && <CompareToggle value={compareMode} onChange={setCompareMode} />}
            </div>

            <div className="tu-revTrend__grid2">
              <ChartSection
                title={compareMode === 'yoy' ? `Dollar Change${inflationLabel}` : `6-Year Revenue Change${inflationLabel}`}
                icon={compareMode === 'yoy' ? 'fas fa-dollar-sign' : 'fas fa-layer-group'}
                description={compareMode === 'yoy' ? 'The dollar difference from the prior year. Green = revenue grew, red = revenue declined.' : 'Revenue 6 years ago on the left, current year on the right.'}>
                <div className="tu-revTrend__chartWrap tu-revTrend__chartWrap--compact"><svg ref={dollarBarRef} className="tu-revTrend__svg" /></div>
                <div className="tu-revTrend__legend">
                    {compareMode === 'yoy' ? (<>
                    <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch" style={{ background: BRAND.green }} />Increase</span>
                    <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch" style={{ background: BRAND.red }} />Decrease</span>
                      <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch tu-revTrend__legendSwatch--line" style={{ background: BRAND.tealDark }} />Trend</span>
                      </>) : (<>
                    <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch tu-revTrend__legendSwatch--line" style={{ background: BRAND.green }} />Revenue Grew</span>
                    <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch tu-revTrend__legendSwatch--line" style={{ background: BRAND.red }} />Revenue Declined</span>
                    <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch" style={{ background: '#CBD5E1' }} />6 Years Prior</span></>)}
                </div>
                <div className="tu-revTrend__tableSpace"><div className="tu-revTrend__tableWrap"><table className="tu-revTrend__table">
                  {compareMode === 'yoy' ? (<>
                        <thead>
                      <tr>
                        <th className="tu-col-year">Year</th>
                        <th style={{ textAlign: 'right' }}>Revenue</th>
                        <th style={{ textAlign: 'right' }}>Prior Year</th>
                        <th style={{ textAlign: 'right' }}>Change</th>
                      </tr>
                    </thead>
                    <tbody>{tableData.map((d) => (<tr key={`dc-${d.report_year}`}>
                      <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                      <td style={{ textAlign: 'right' }}>{d._amtL !== null ? formatCurrency(d._amtL) : '—'}</td>
                      <td style={{ textAlign: 'right', color: d._amtLc !== null && d._amtLc > 0 ? BRAND.green : d._amtLc !== null && d._amtLc < 0 ? BRAND.red : BRAND.mutedGrey, fontWeight: 600 }}>
                        {d._amtLc !== null ? `${d._amtLc >= 0 ? '+' : ''}${formatCurrency(d._amtLc)}` : '—'}</td>
                    </tr>))}</tbody>
                  </>) : (<>
                        <thead>
                      <tr>
                        <th className="tu-col-year">Year</th>
                        <th style={{ textAlign: 'right' }}>Revenue</th>
                        <th style={{ textAlign: 'right' }}>6 Yrs Prior</th>
                        <th style={{ textAlign: 'right' }}>Change</th>
                      </tr>
                    </thead>
                    <tbody>{sortDesc(sixYrRows).map((d) => (<tr key={`6dc-${d.report_year}`}>
                      <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                      <td style={{ textAlign: 'right' }}>{d._amtL6 !== null ? formatCurrency(d._amtL6) : '—'}</td>
                      <td style={{ textAlign: 'right', color: d._amtLc6 !== null && d._amtLc6 > 0 ? BRAND.green : d._amtLc6 !== null && d._amtLc6 < 0 ? BRAND.red : BRAND.mutedGrey, fontWeight: 600 }}>
                        {d._amtLc6 !== null ? `${d._amtLc6 >= 0 ? '+' : ''}${formatCurrency(d._amtLc6)}` : '—'}</td>
                    </tr>))}</tbody>
                  </>)}
                </table></div></div>
              </ChartSection>

              <ChartSection
                title={compareMode === 'yoy' ? `Percent Change${inflationLabel}` : `6-Year Revenue Change (%)${inflationLabel}`}
                icon="fas fa-percentage"
                description={compareMode === 'yoy' ? 'The percentage change from the prior year.' : 'The same 6-year comparison as a percentage.'}>
                <div className="tu-revTrend__chartWrap tu-revTrend__chartWrap--compact"><svg ref={pctBarRef} className="tu-revTrend__svg" /></div>
                <div className="tu-revTrend__legend">
                    {compareMode === 'yoy' ? (<>
                    <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch" style={{ background: BRAND.green }} />Increase</span>
                    <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch" style={{ background: BRAND.red }} />Decrease</span>
                      <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch tu-revTrend__legendSwatch--line" style={{ background: BRAND.tealDark }} />Trend</span>
                      </>) : (<>
                    <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch tu-revTrend__legendSwatch--line" style={{ background: BRAND.green }} />Revenue Grew</span>
                    <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch tu-revTrend__legendSwatch--line" style={{ background: BRAND.red }} />Revenue Declined</span>
                    <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch" style={{ background: '#CBD5E1' }} />6 Years Prior</span></>)}
                </div>
                <div className="tu-revTrend__tableSpace"><div className="tu-revTrend__tableWrap"><table className="tu-revTrend__table">
                  {compareMode === 'yoy' ? (<>
                        <thead>
                      <tr>
                        <th className="tu-col-year">Year</th>
                        <th style={{ textAlign: 'right' }}>Revenue</th>
                        <th style={{ textAlign: 'right' }}>Change</th>
                        <th style={{ textAlign: 'right' }}>% Change</th>
                      </tr>
                    </thead>
                    <tbody>{tableData.map((d) => (<tr key={`pct-${d.report_year}`}>
                      <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                      <td style={{ textAlign: 'right' }}>{d._amtLc !== null ? `${d._amtLc >= 0 ? '+' : ''}${formatCurrency(d._amtLc)}` : '—'}</td>
                      <td style={{ textAlign: 'right', color: d._amtLcp !== null && d._amtLcp > 0 ? BRAND.green : d._amtLcp !== null && d._amtLcp < 0 ? BRAND.red : BRAND.mutedGrey, fontWeight: 700 }}>
                        {d._amtLcp !== null ? (<><i className={`fas fa-arrow-${d._amtLcp >= 0 ? 'up' : 'down'}`} style={{ fontSize: '0.75rem', marginRight: 4 }} />{fmtPct(Math.abs(d._amtLcp))}</>) : '—'}</td>
                    </tr>))}</tbody>
                  </>) : (<>
                        <thead>
                      <tr>
                        <th className="tu-col-year">Year</th>
                        <th style={{ textAlign: 'right' }}>Revenue</th>
                        <th style={{ textAlign: 'right' }}>6-Yr Change</th>
                        <th style={{ textAlign: 'right' }}>6-Yr %</th>
                      </tr>
                    </thead>
                    <tbody>{sortDesc(sixYrRows).map((d) => (<tr key={`6pct-${d.report_year}`}>
                      <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                      <td style={{ textAlign: 'right' }}>{d._amtLc6 !== null ? `${d._amtLc6 >= 0 ? '+' : ''}${formatCurrency(d._amtLc6)}` : '—'}</td>
                      <td style={{ textAlign: 'right', color: d._amtLcp6 !== null && d._amtLcp6 > 0 ? BRAND.green : d._amtLcp6 !== null && d._amtLcp6 < 0 ? BRAND.red : BRAND.mutedGrey, fontWeight: 700 }}>
                        {d._amtLcp6 !== null ? (<><i className={`fas fa-arrow-${d._amtLcp6 >= 0 ? 'up' : 'down'}`} style={{ fontSize: '0.75rem', marginRight: 4 }} />{fmtPct(Math.abs(d._amtLcp6))}</>) : '—'}</td>
                    </tr>))}</tbody>
                  </>)}
                </table></div></div>
              </ChartSection>
            </div>

            {perCapitaData.length > 0 && (
              <ChartSection title={`Revenue Per Capita${inflationLabel}`} icon="fas fa-user-friends" description="Total revenue divided by population estimate.">
                <div className="tu-revTrend__chartWrap"><svg ref={perCapitaBarRef} className="tu-revTrend__svg" /></div>
                <div className="tu-revTrend__legend">
                  <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch" style={{ background: BRAND.teal }} />Per Capita</span>
                  <span className="tu-revTrend__legendItem"><span className="tu-revTrend__legendSwatch tu-revTrend__legendSwatch--line" style={{ background: BRAND.tealDark }} />Trend</span>
                </div>
                <div className="tu-revTrend__tableSpace"><div className="tu-revTrend__tableWrap"><table className="tu-revTrend__table">
                      <thead>
                    <tr>
                      <th className="tu-col-year">Year</th>
                      <th style={{ textAlign: 'right' }}>Revenue</th>
                      <th style={{ textAlign: 'right' }}>Population</th>
                      <th style={{ textAlign: 'right' }}>Per Capita</th>
                    </tr>
                  </thead>
                  <tbody>{[...perCapitaData].sort((a, b) => b.report_year - a.report_year).map((d) => (
                    <tr key={`pc-${d.report_year}`}>
                      <td className="tu-col-year tu-reports__primary">{d.report_year}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(d._amt)}</td>
                      <td style={{ textAlign: 'right' }}>{d.population.toLocaleString()}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, color: BRAND.tealDark }}>{formatCurrency(d.perCapita)}</td>
                    </tr>
                  ))}</tbody>
                </table></div></div>
              </ChartSection>
            )}

          </div>
        )}
      </Col>
    </Row>
  );
};

export default EntityTotalRevenueOverview;
